import { joiResolver } from '@hookform/resolvers/joi';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AdminContent, AdminInnerBox } from '../../atoms/AdminPageParts';

import CancelButton from '@/components/atoms/CancelButton';
import LoadingBackdrop from '@/components/atoms/LoadingBackdrop';
import { PositiveIntegerField } from '@/components/atoms/PositiveIntegerField';
import useLocalizedJoi from '@/components/hooks/useLocalizedJoi';
import { PasswordPolicyButton } from '@/components/molecules/PasswordPolicyButton';
import BreadcrumbBar from '@/components/organisms/BreadcrumbBar';
import { useVadPresetsQuery } from '@/types/graphql';
import { getPasswordValidator } from '@/utils/joi';

export type TenantCompanyData = {
  guideManagerName: string;
  userName: string;
  temporaryPassword: string;
  roomLimit: number;
  roomCreationLimit: number;
  isEnableVoice: boolean;
  vadPresetId?: string;
  note?: string;
};

export type TenantCompanyError =
  | {
      type: 'UserNameAlreadyExists';
      message: string;
    }
  | {
      type: 'ExcessTenantCompanyRoomNumberLimit';
      message: string;
      limit: number;
    };

export const TenantForm = (props: {
  ownerCompanyName: string;
  loading: boolean;
  onSubmit: (input: TenantCompanyData) => Promise<TenantCompanyError[]>;
  defaultData?: TenantCompanyData; // 新規登録は defaultDataなし
  children?: ReactElement;
  requirePassword?: boolean;
}) => {
  const joi = useLocalizedJoi();
  const vadPresets = useVadPresetsQuery({ variables: { input: {} } });
  const vadPresetsLocal = vadPresets.data?.vadPresets?.slice();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    setError,
    control,
  } = useForm<TenantCompanyData>({
    defaultValues: {
      isEnableVoice: false,
      ...props.defaultData,
      vadPresetId: props.defaultData?.vadPresetId ?? '',
    },
    resolver: joiResolver(
      joi.object<TenantCompanyData>({
        userName: joi
          .string()
          .trim()
          .max(50)
          .required()
          .pattern(/^[ -~]+$/, 'username'),
        temporaryPassword: props.requirePassword
          ? getPasswordValidator(joi).required()
          : getPasswordValidator(joi).allow(''),
        guideManagerName: joi.string().trim().max(50).required(),
        isEnableVoice: joi.boolean(),
        roomLimit: joi.number().min(0).max(100).required(),
        roomCreationLimit: joi.number().min(0).max(100).required(),
        vadPresetId: joi.string().uuid().required(),
        note: joi.string().allow('').max(200),
      })
    ),
  });

  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');

  useEffect(() => {
    if (props.defaultData != null) {
      reset(props.defaultData);
    }
  }, [props.defaultData, reset]);

  return (
    <Box>
      <LoadingBackdrop open={props.loading} />
      <BreadcrumbBar keyFrom="/admin/tenant" />
      <AdminContent>
        <AdminInnerBox>
          {props.defaultData ? (
            <Typography variant="h4">{`${
              props.defaultData.guideManagerName
            } ${tCommon('command.edit')}`}</Typography>
          ) : (
            <Typography variant="h4">{tCommon('command.new')}</Typography>
          )}
        </AdminInnerBox>
        <AdminInnerBox
          component="form"
          onSubmit={handleSubmit(async (input, ev) => {
            ev?.preventDefault();
            const errors = await props.onSubmit(input);
            if (errors.length >= 1) {
              errors.forEach((e) => {
                if (e.type === 'UserNameAlreadyExists') {
                  setError('userName', {
                    message: 'ユーザー名は既に存在します',
                  });
                } else if (e.type === 'ExcessTenantCompanyRoomNumberLimit') {
                  setError('roomLimit', {
                    message: `アクティブ可能ルーム数の上限値 ${e.limit} 以下を設定してください`,
                  });
                }
              });
            }
          })}
          sx={{ bgcolor: (t) => t.palette.common.white }}
        >
          <Stack sx={{ maxWidth: '320px', mx: 'auto', mt: 4 }} spacing={3}>
            {/* 企業名 */}
            <TextField
              size="small"
              value={props.ownerCompanyName}
              disabled
              label="企業名"
            />
            {/* ユーザー名 */}
            <TextField
              size="small"
              {...register('userName')}
              label={t('company.email')}
              error={'userName' in errors}
              helperText={errors.userName?.message}
              InputLabelProps={{ shrink: !!watch('userName') }}
            />
            {/* ユーザー一時パスワード */}
            <TextField
              type="password"
              size="small"
              {...register('temporaryPassword')}
              label={t('company.tempraryPassword')}
              error={'temporaryPassword' in errors}
              helperText={errors.temporaryPassword?.message}
              InputLabelProps={{ shrink: !!watch('temporaryPassword') }}
              InputProps={{
                endAdornment: <PasswordPolicyButton />,
              }}
            />
            {/* ガイド管理者名*/}
            <TextField
              size="small"
              {...register('guideManagerName')}
              label={t('company.guideManagerName')}
              error={'guideManagerName' in errors}
              helperText={errors.guideManagerName?.message}
              InputLabelProps={{ shrink: !!watch('guideManagerName') }}
            />

            {/* アクティブルーム数 */}
            <Stack spacing={1}>
              <PositiveIntegerField
                {...register('roomLimit')}
                error={'roomLimit' in errors}
                helperText={errors.roomLimit?.message}
                size="small"
                label={t('company.roomLimit')}
              />
              <Typography variant="caption">
                {t('common.0forUnlimited')}
              </Typography>
            </Stack>

            {/* ルーム作成可能数上限 */}
            <Stack spacing={1}>
              <PositiveIntegerField
                {...register('roomCreationLimit')}
                error={'roomCreationLimit' in errors}
                helperText={errors.roomCreationLimit?.message}
                size="small"
                label={t('company.roomCreationLimit')}
              />
              <Typography variant="caption">
                {t('common.0forUnlimited')}
              </Typography>
            </Stack>

            {/* 音声読み上げのデフォルト値 */}
            <Controller
              control={control}
              name="isEnableVoice"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>{t('company.voiceDefault')}</FormLabel>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t('company.enableVoice')}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={t('company.disableVoice')}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
            {/* 利用環境 */}
            <Controller
              name="vadPresetId"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl size="small">
                  <InputLabel id="vad-preset-label">利用環境</InputLabel>
                  <Select
                    labelId="vad-preset-label"
                    label="利用環境"
                    {...field}
                  >
                    {vadPresetsLocal
                      ?.sort((a) => (a.default ? -1 : 1))
                      .map((x) => (
                        <MenuItem key={x.id} value={x.id}>
                          {x.label}
                        </MenuItem>
                      )) ?? []}
                  </Select>
                  <FormHelperText error={fieldState.error?.message != null}>
                    {fieldState.error?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
          <Stack direction={'row'} justifyContent={'center'} sx={{ py: 3 }}>
            <CancelButton
              onClick={() => navigate('/admin/tenant')}
              sx={{ mx: 'auto', display: 'block', m: 1 }}
            />
            <Button
              type="submit"
              sx={{ mx: 'auto', display: 'block', m: 1 }}
              disabled={!isDirty}
            >
              {props.defaultData
                ? tCommon('command.saveChanges')
                : tCommon('command.register')}
            </Button>
          </Stack>
        </AdminInnerBox>
        {props.children}
      </AdminContent>
    </Box>
  );
};
